import React, { useEffect } from "react";
import { Bell, ClipboardList, History, House, Plus, UserRound } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import RushLogo from "../assets/png/rush-logo.png";
import { useNotificationStore } from "../features/notifications/notificationStore";
import { useAuthStore } from "../features/auth/authStore";

// Desktop navigation; phones use the bottom tab bar
const NAV_LINKS = [
  { to: "/home", label: "Home", icon: House },
  { to: "/order/track", label: "Track", icon: ClipboardList },
  { to: "/order/history", label: "History", icon: History },
  { to: "/profile", label: "Profile", icon: UserRound },
];

const TopNavBar = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const user = useAuthStore((s) => s.user);
  const fetchNotifications = useNotificationStore((s) => s.fetchNotifications);
  const unread = useNotificationStore((s) => s.unreadCount());

  // Refresh the badge whenever the customer moves between screens
  useEffect(() => {
    if (pathname !== "/notifications") void fetchNotifications();
  }, [pathname, fetchNotifications]);

  const initials = [user?.first_name?.[0], user?.last_name?.[0]].filter(Boolean).join("").toUpperCase();

  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-gray-100">
      <div className="h-16 md:h-[72px] px-4 md:px-6 max-w-6xl mx-auto flex items-center gap-3">
        <Link to="/home" className="flex items-center shrink-0">
          <img src={RushLogo} alt="Rush" className="h-6 w-auto object-contain" />
        </Link>

        <nav className="hidden md:flex items-center gap-1 ml-8" aria-label="Main">
          {NAV_LINKS.map((link) => {
            const active = pathname === link.to || (link.to === "/profile" && pathname.startsWith("/profile"));
            const Icon = link.icon;
            return (
              <Link
                key={link.to}
                to={link.to}
                aria-current={active ? "page" : undefined}
                className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-medium transition-colors ${
                  active ? "bg-[#44B6CE]/10 text-[#1A8FAA]" : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                }`}
              >
                <Icon className="h-4 w-4" />
                {link.label}
              </Link>
            );
          })}
        </nav>

        <div className="ml-auto flex items-center gap-2 md:gap-3">
          <button
            type="button"
            onClick={() => navigate("/order/new")}
            className="hidden md:flex items-center gap-2 h-10 px-4 rounded-xl bg-[#44B6CE] hover:bg-[#1A8FAA] text-white text-sm font-semibold shadow-sm transition"
          >
            <Plus className="h-4 w-4" /> New order
          </button>

          <button
            type="button"
            onClick={() => navigate("/notifications")}
            className="relative h-10 w-10 flex items-center justify-center rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200 transition focus:outline-none focus:ring-2 focus:ring-gray-300"
            aria-label={unread > 0 ? `Notifications, ${unread} unread` : "Notifications"}
          >
            <Bell className="h-5 w-5" />
            {unread > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </button>

          <Link
            to="/profile"
            className="hidden md:flex h-10 w-10 items-center justify-center rounded-full bg-[#44B6CE] text-white text-sm font-bold"
            aria-label="Profile"
          >
            {initials || <UserRound className="h-4 w-4" />}
          </Link>
        </div>
      </div>
    </header>
  );
};

export default TopNavBar;
